import { useState, useEffect } from 'react';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface RegistrationContent {
  id: string;
  content_key: string;
  content_type: 'text' | 'number' | 'array' | 'object';
  content_value: any;
  display_order: number;
  is_active: boolean;
}

interface FormState {
  content_key: string;
  content_type: 'text' | 'number' | 'array' | 'object';
  content_value: string;
  display_order: number;
  is_active: boolean;
}

function getLabel(key: string) {
  return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function previewValue(content: RegistrationContent): string {
  const v = content.content_value;
  if (content.content_type === 'text' || content.content_type === 'number') return String(v ?? '');
  if (Array.isArray(v)) return `${v.length} item${v.length !== 1 ? 's' : ''}`;
  if (typeof v === 'object' && v !== null) return JSON.stringify(v).slice(0, 60) + '...';
  return String(v ?? '');
}

export default function MobileRegistrationContentManager() {
  const [items, setItems] = useState<RegistrationContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<RegistrationContent | null>(null);
  const [form, setForm] = useState<FormState>({
    content_key: '',
    content_type: 'text',
    content_value: '',
    display_order: 1,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RegistrationContent | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('registration_content')
        .select('*')
        .order('display_order', { ascending: true });
      if (error) throw error;
      setItems(data || []);
    } catch {
      toast.error('Failed to load registration content');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({
      content_key: '',
      content_type: 'text',
      content_value: '',
      display_order: items.length + 1,
      is_active: true,
    });
    setSheetOpen(true);
  };

  const openEdit = (item: RegistrationContent) => {
    setEditing(item);
    const valStr = typeof item.content_value === 'string'
      ? item.content_value
      : JSON.stringify(item.content_value, null, 2);
    setForm({
      content_key: item.content_key,
      content_type: item.content_type,
      content_value: valStr,
      display_order: item.display_order,
      is_active: item.is_active,
    });
    setSheetOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      let parsed: any;
      try { parsed = JSON.parse(form.content_value); }
      catch { parsed = form.content_value; }

      const payload = {
        content_key: form.content_key,
        content_type: form.content_type,
        content_value: parsed,
        display_order: form.display_order,
        is_active: form.is_active,
        updated_at: new Date().toISOString(),
      };

      if (editing) {
        const { error } = await supabase.from('registration_content').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('registration_content').insert([payload]);
        if (error) throw error;
      }

      await fetchItems();
      setSheetOpen(false);
      toast.success(editing ? 'Content updated' : 'Content added');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item: RegistrationContent) => {
    try {
      const { error } = await supabase
        .from('registration_content')
        .update({ is_active: !item.is_active })
        .eq('id', item.id);
      if (error) throw error;
      await fetchItems();
      toast.success(item.is_active ? 'Deactivated' : 'Activated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const { error } = await supabase.from('registration_content').delete().eq('id', deleteTarget.id);
      if (error) throw error;
      await fetchItems();
      setDeleteTarget(null);
      toast.success('Content deleted');
    } catch {
      toast.error('Failed to delete content');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
          {items.length} item{items.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={openAdd}
          className="flex items-center gap-1.5 px-3 py-2 bg-golf-orange text-white text-xs font-semibold rounded-xl active:bg-golf-orange/90 transition-colors"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Add Content
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-10 text-center">
          <p className="text-sm text-gray-400">No registration content yet</p>
          <button onClick={openAdd} className="mt-2 text-xs text-golf-orange font-medium">
            Add the first item
          </button>
        </div>
      ) : (
        items.map(item => (
          <div key={item.id} className={`bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3.5 ${!item.is_active ? 'opacity-60' : ''}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-sm font-semibold text-golf-gray">{getLabel(item.content_key)}</p>
                  <span className="text-xs text-gray-400 capitalize">{item.content_type}</span>
                  <span className="text-xs text-gray-400">#{item.display_order}</span>
                </div>
                <p className="text-xs text-gray-400 line-clamp-2">{previewValue(item)}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleToggleActive(item)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                    item.is_active ? 'bg-green-50 active:bg-green-100' : 'bg-gray-100 active:bg-gray-200'
                  }`}
                >
                  {item.is_active
                    ? <CheckCircleIcon className="w-4 h-4 text-green-600" />
                    : <XCircleIcon className="w-4 h-4 text-gray-400" />
                  }
                </button>
                <button
                  onClick={() => openEdit(item)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-50 active:bg-blue-100 transition-colors"
                >
                  <PencilIcon className="w-3.5 h-3.5 text-blue-600" />
                </button>
                <button
                  onClick={() => setDeleteTarget(item)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 active:bg-red-100 transition-colors"
                >
                  <TrashIcon className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            </div>
          </div>
        ))
      )}

      {sheetOpen && (
        <div className="fixed inset-x-0 top-0 bottom-20 z-40 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSheetOpen(false)} />
          <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="text-base font-bold text-golf-gray">{editing ? 'Edit Content' : 'Add Content'}</h2>
                <p className="text-xs text-gray-400 mt-0.5">Registration page content</p>
              </div>
              <button onClick={() => setSheetOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
                <XMarkIcon className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSave} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Content Key</label>
                <input
                  type="text"
                  value={form.content_key}
                  onChange={e => setForm(prev => ({ ...prev, content_key: e.target.value }))}
                  placeholder="e.g. registration_fee"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                  required
                  disabled={!!editing}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Type</label>
                  <select
                    value={form.content_type}
                    onChange={e => setForm(prev => ({ ...prev, content_type: e.target.value as any }))}
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 appearance-none"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="array">Array</option>
                    <option value="object">Object</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Order</label>
                  <input
                    type="number"
                    value={form.display_order}
                    onChange={e => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Content Value</label>
                <textarea
                  value={form.content_value}
                  onChange={e => setForm(prev => ({ ...prev, content_value: e.target.value }))}
                  rows={6}
                  placeholder="Enter value (use JSON for arrays/objects)"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 font-mono resize-none"
                  required
                />
              </div>

              <div className="flex items-center gap-3 pb-2">
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`w-10 h-6 rounded-full transition-colors ${form.is_active ? 'bg-golf-orange' : 'bg-gray-300'}`}
                >
                  <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${form.is_active ? 'translate-x-4' : ''}`} />
                </button>
                <span className="text-sm text-gray-600">Active</span>
              </div>
            </form>

            <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="reg-form"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors"
              >
                {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Content'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
          <div className="relative w-full bg-white rounded-t-3xl px-5 py-6 space-y-4 shadow-2xl">
            <h3 className="text-base font-bold text-golf-gray">Delete Content?</h3>
            <p className="text-sm text-gray-500">
              Are you sure you want to delete <span className="font-semibold text-golf-gray">"{getLabel(deleteTarget.content_key)}"</span>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-semibold active:bg-red-600 disabled:opacity-60"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
